import type { FieldValues } from 'react-hook-form';
import { Input } from '../Input';
import { PasswordInput } from '../PasswordInput';
import { Textarea } from '../Textarea';
import { FormField } from './FormField';
import { asText, type BaseFieldProps, describedBy, useBoundField, useFieldIds } from './fieldBase';

// ——— text / email / number / password ———

export interface TextFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
  /** Максимум символов — показывается счётчик «N / max». */
  maxLength?: number;
  type?: 'text' | 'email' | 'url' | 'tel';
  autoComplete?: string;
}

export function TextField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  placeholder,
  maxLength,
  type = 'text',
  autoComplete,
}: TextFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const value = asText(field.value);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      counter={maxLength ? { length: value.length, max: maxLength } : undefined}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <Input
        id={ids.id}
        ref={field.ref}
        name={field.name}
        type={type}
        value={value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete={autoComplete}
        disabled={disabled || field.disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}

export interface NumberFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
}

/** Число: ввод строкой (цифры, пробелы и знаки), разбор — в zod-схеме формы. */
export function NumberField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  placeholder,
}: NumberFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <Input
        id={ids.id}
        ref={field.ref}
        name={field.name}
        inputMode="decimal"
        value={asText(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        placeholder={placeholder}
        disabled={disabled || field.disabled}
        className="tabular"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}

export interface PasswordFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
  autoComplete?: 'current-password' | 'new-password';
}

export function PasswordField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  placeholder,
  autoComplete,
}: PasswordFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <PasswordInput
        id={ids.id}
        ref={field.ref}
        name={field.name}
        value={asText(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled || field.disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}

// ——— textarea ———

export interface TextareaFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
  maxLength?: number;
  rows?: number;
}

export function TextareaField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide = true,
  disabled,
  placeholder,
  maxLength,
  rows,
}: TextareaFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const value = asText(field.value);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      counter={maxLength ? { length: value.length, max: maxLength } : undefined}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <Textarea
        id={ids.id}
        ref={field.ref}
        name={field.name}
        value={value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        disabled={disabled || field.disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}
